import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, Play, Pause, Maximize, Minimize } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

function VideoPreviewModal({ asset, onClose }) {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const modalRef = useRef(null);
  const isClosingRef = useRef(false);
  
  // Detect mobile/touch devices where fullscreen API isn't supported
  const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window || navigator.maxTouchPoints > 0;

// Fetch signed URL on mount (skip if already provided)
useEffect(() => {
  const fetchSignedUrl = async () => {
    if (asset.signedUrl) {
      setSignedUrl(asset.signedUrl);
      setIsLoading(false);
      return;
    }

    try {
      const key = asset.key || asset.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '');
      if (!key) {
        console.error('No key found for asset');
        setIsLoading(false);
        return;
      }

      const functions = getFunctions();
      const getSignedUrls = httpsCallable(functions, 'getSignedUrls');
      const result = await getSignedUrls({ keys: [key] });
      
      if (result.data?.signedUrls?.[key]) {
        setSignedUrl(result.data.signedUrls[key]);
      }
    } catch (error) {
      console.error('Failed to get signed URL:', error);
    } finally {
      setIsLoading(false);
    }
  };

  fetchSignedUrl();
}, [asset]);

  // Video teardown only happens in handleClose, not in effect cleanup
  const handleClose = useCallback(() => {
    isClosingRef.current = true;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    // Reset closing flag when modal opens
    isClosingRef.current = false;

    // Handle escape key to close
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      // NO video teardown here - only in handleClose
    };
  }, [handleClose]);

  const togglePlay = () => {
    if (videoRef.current && !isClosingRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play()
          .then(() => {
            if (!isClosingRef.current) {
              setIsPlaying(true);
            }
          })
          .catch(err => {
            if (err.name === 'AbortError') return;
            if (isClosingRef.current) return;
            console.error('Play error:', err);
          });
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isClosingRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current && !isClosingRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (progressRef.current && videoRef.current && !isClosingRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleProgressDrag = (e) => {
    e.preventDefault();
    
    const handleDrag = (moveEvent) => {
      if (progressRef.current && videoRef.current && !isClosingRef.current) {
        const rect = progressRef.current.getBoundingClientRect();
        const clickX = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
        const percentage = clickX / rect.width;
        const newTime = percentage * duration;
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleMouseUp);
    handleDrag(e);
  };

  const handleProgressTouch = (e) => {
    e.preventDefault();
    
    const handleTouchMove = (moveEvent) => {
      if (progressRef.current && videoRef.current && !isClosingRef.current) {
        const rect = progressRef.current.getBoundingClientRect();
        const touchX = moveEvent.touches[0].clientX;
        const clickX = Math.max(0, Math.min(touchX - rect.left, rect.width));
        const percentage = clickX / rect.width;
        const newTime = percentage * duration;
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    handleTouchMove(e);
  };

  const toggleFullscreen = () => {
    // Don't attempt fullscreen on mobile - API not supported
    if (isMobile) return;
    
    try {
      if (!document.fullscreenElement) {
        modalRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.warn('Fullscreen not supported:', err);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      className="video-modal-overlay"
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
    >
      <div 
        ref={modalRef}
        className="video-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#000',
          borderRadius: '0px',
          maxWidth: '900px',
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
        >
          <X size={24} />
        </button>

        {/* Video container */}
        <div 
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16/9',
            background: '#000'
          }}
        >
          {isLoading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'white'
            }}>
              Loading...
            </div>
          ) : signedUrl ? (
            <>
              <video
                ref={videoRef}
                src={signedUrl}
                onError={(e) => {
                  // Ignore abort errors (happens when closing modal before video loads)
                  if (e.target.error?.code === e.target.error?.MEDIA_ERR_ABORTED) {
                    return;
                  }
                  if (isClosingRef.current) return;
                  console.error('Video error:', e.target.error);
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => !isClosingRef.current && setIsPlaying(true)}
                onPause={() => !isClosingRef.current && setIsPlaying(false)}
                onEnded={() => !isClosingRef.current && setIsPlaying(false)}
                onClick={togglePlay}
                playsInline
              />

              {/* Play button overlay when paused */}
              {!isPlaying && (
                <div
                  onClick={togglePlay}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80px',
                    height: '80px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  <Play size={36} color="white" style={{ marginLeft: '4px' }} />
                </div>
              )}
            </>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'white'
            }}>
              Failed to load video
            </div>
          )}

          {/* Controls bar */}
          {signedUrl && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
                padding: '40px 16px 16px'
              }}
            >
              {/* Progress bar */}
              <div
                ref={progressRef}
                onClick={handleProgressClick}
                onMouseDown={handleProgressDrag}
                onTouchStart={handleProgressTouch}
                style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '3px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    width: `${progressPercentage}%`,
                    height: '100%',
                    background: '#d32f2f',
                    borderRadius: '3px',
                    transition: 'width 0.1s'
                  }}
                />
                {/* Scrubber dot */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: `${progressPercentage}%`,
                    transform: 'translate(-50%, -50%)',
                    width: '14px',
                    height: '14px',
                    background: '#d32f2f',
                    borderRadius: '50%',
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                />
              </div>

              {/* Controls row */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Play/Pause button */}
                  <button
                    onClick={togglePlay}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                  </button>

                  {/* Time display */}
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                {/* Fullscreen button - hide on mobile */}
                {!isMobile && (
                  <button
                    onClick={toggleFullscreen}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Asset name below video */}
        <div
          style={{
            padding: '12px 16px',
            color: 'white',
            fontSize: '14px',
            borderTop: '1px solid #333'
          }}
        >
          {asset.name}
        </div>
      </div>
    </div>
  );
}

export default VideoPreviewModal;